import { DeviceEventEmitter, Platform } from 'react-native';

import eventBus from '@navigation/eventBus';

/**
 * Hosts served by the TaskTag web app. The same hosts are declared as iOS
 * Universal Links (`ios.associatedDomains`) and Android App Links
 * (`android.intentFilters`) in app.config.js, so links to them belong to this
 * app rather than to the browser.
 */
export const TASKTAG_APP_HOSTS = ['app.tasktag.com', 'beta.tasktag.com'];

/**
 * Emitted after an in-app tap on a TaskTag link has been pushed through the
 * deep-link listeners. app-stack listens for it to run the invitation flow,
 * which otherwise only runs on cold start or on a background→active
 * transition — neither happens when the link is tapped while the app is
 * already in the foreground.
 */
export const INTERNAL_DEEP_LINK_EVENT = 'internal-deep-link';

/**
 * Extracts the lowercased hostname from an absolute http(s) URL. Userinfo and
 * port are stripped so 'https://someone@app.tasktag.com:443/x' resolves to
 * 'app.tasktag.com'. Returns '' for anything that is not an absolute http(s)
 * URL. Hand-rolled instead of `new URL()` because React Native's URL polyfill
 * is incomplete and throws on inputs Safari accepts.
 */
const getHost = (url: string): string => {
  const match = /^https?:\/\/([^/?#]+)/i.exec(url.trim());
  if (!match) return '';
  return match[1].split('@').pop()?.split(':')[0].toLowerCase() ?? '';
};

export const isTaskTagAppUrl = (url?: string | null): boolean =>
  !!url && TASKTAG_APP_HOSTS.includes(getHost(url));

/**
 * Paths the in-app invitation flow actually understands. Every other TaskTag
 * link (a shared project report, a marketing page, anything added to the web
 * app later) keeps going to the OS handler: intercepting those would swallow
 * the tap with nothing to show for it and persist junk invitation state, since
 * the invite parser falls back to positional path slicing when it finds no
 * invite query params.
 */
const HANDLED_PATH_PREFIXES = ['/invited-user', '/invite/group'];

/** Path of an absolute http(s) URL, without query or fragment. '' if absent. */
const getPath = (url: string): string => {
  const afterHost = url.trim().replace(/^https?:\/\/[^/?#]+/i, '');
  if (!afterHost.startsWith('/')) return '';
  return afterHost.split(/[?#]/)[0].replace(/\/+$/, '');
};

/**
 * True when the URL is a TaskTag link this app knows how to open itself.
 */
export const isInAppTaskTagUrl = (url?: string | null): boolean => {
  if (!isTaskTagAppUrl(url)) return false;

  const path = getPath(url as string);
  return HANDLED_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
};

/**
 * Handles a tapped TaskTag link inside the app instead of handing it to the OS.
 *
 * iOS never re-opens the calling app for its own Universal Link: `openURL` on
 * https://app.tasktag.com/invited-user?... from inside TaskTag always lands in
 * Safari, while Android resolves the same URL back into the app through its
 * verified App Link. Re-emitting the URL on the `url` device event feeds it to
 * the exact listeners a real deep link hits (App.tsx, auth-stack and React
 * Navigation's linking subscriber), which gives iOS the Android behaviour
 * without an OS round trip.
 *
 * Only the invite paths this app can act on are intercepted — see
 * isInAppTaskTagUrl.
 *
 * Returns true when the link was handled here, false when the caller should
 * open it the usual way.
 */
export const handleTaskTagUrlInApp = (url: string): boolean => {
  if (Platform.OS === 'web' || !isInAppTaskTagUrl(url)) return false;

  DeviceEventEmitter.emit('url', { url });
  eventBus.emit(INTERNAL_DEEP_LINK_EVENT, url);

  return true;
};
